import { groupBySection, packColumns, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent } from '../types.js';
import type { MenuTemplate } from './common.js';
import { IW_FAMILY, IW_THEME, IW_VARIANTS, iwDishesCss, iwRootCss, iwScriptHeight } from './ink-wall-panels.js';
import { iwBlock, iwBlockCss, iwBlockHeight, iwTitle, iwTitleCss } from './ink-wall-hero.js';

/**
 * Ink Wall, layout C: the menu wall without photos. The board's title in huge brush script
 * across the top, then sections in three columns, each under its script heading, as size
 * tables (SINGLE / DOUBLE) where the dishes come in sizes and as leader lists elsewhere, with
 * topping chips under them. For burgers, sides and drinks by size.
 */

const S = '.mb-t-ink-wall-sizes';
const TITLE_EM = 6.4;
/** Portrait sets its two columns larger, so they fill the tall screen. */
const PORTRAIT_SCALE = 1.35;

function columns(board: MenuBoardContent, portrait: boolean): MenuColumn[] {
  const brand = board.brand.name || board.brand.nameAr ? 1.3 * 1.4 + 0.3 : 0;
  const title = brand + iwScriptHeight(board, board.settings.title ?? board.name, board.settings.titleAr, TITLE_EM, portrait ? 14 : 26, 2) + 1.8;
  const capacity = portrait ? (120 - 5 - title) / PORTRAIT_SCALE : 67.5 - 5 - title;
  const column: MenuColumn = { capacity, blockCost: iwBlockHeight(board, portrait ? 36 : 56, portrait ? 20 : 33) };
  return Array.from({ length: portrait ? 2 : 3 }, () => column);
}

export const inkWallSizes: MenuTemplate = {
  id: 'ink-wall-sizes',
  name: 'Ink Wall · Size tables',
  description:
    'The Ink Wall look without photos: a huge brush-script title, then sections in three columns as size tables (Single / Double) or leader lists, with topping chips. Matches the Ink Wall panels.',
  defaultTheme: IW_THEME,
  variants: IW_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { ...IW_FAMILY, layout: 'Size tables' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), columns(board, orientation === 'portrait'), { balance: true });
  },

  render(ctx) {
    const cols = ctx.page.columns.map((c) => `<div class="iw3-col">${c.map((b) => iwBlock(ctx, b, 'iw3')).join('')}</div>`).join('');
    return `${iwTitle(ctx, 'iw3', ctx.orientation === 'portrait' ? 14 : 26)}<div class="iw3-cols">${cols}</div>`;
  },

  css: `
${iwRootCss(S)}
${S}{padding:2.5em 3.5em}
${iwDishesCss(S)}
${iwTitleCss(S, 'iw3', TITLE_EM)}
${iwBlockCss(S, 'iw3')}
${S} .iw3-head{text-align:center}
${S} .iw3-sts .mb-2nd{text-align:left}
${S} .iw3-cols{flex:1;min-height:0;display:grid;grid-template-columns:repeat(3,minmax(0,1fr));column-gap:4em}
${S}.mb-portrait .iw3-cols{font-size:${PORTRAIT_SCALE}em;grid-template-columns:repeat(2,minmax(0,1fr));column-gap:2.6em}
${S} .iw3-col{min-width:0;display:flex;flex-direction:column}
`,
};
